import { uploadTelemetryService } from "@/utils/uploadTelemetry";
import { UPLOAD_EVENT } from "../diagnostics/UploadEvents";

/**
 * Wire an already-running chunked upload into its UploadDiagnostics.
 *
 * The chunked uploader emits two orthogonal streams:
 *   1. `uploadTelemetryService.subscribe(uploadId, listener)` — package-level
 *      status transitions + throughput (STATUS = UPLOADING | WAITING | SLOW |
 *      STALLED | PAUSED | FINALIZING | COMPLETED | FAILED)
 *   2. `ChunkedUploadService.subscribePerFileProgress(uploadId, listener)` —
 *      per-file granularity (chunk_started, chunk_completed, retries,
 *      stallState transitions)
 *
 * This bridge subscribes to both and translates them into UPLOAD_EVENT.*
 * calls on the diagnostics facade. We deliberately keep the mapping table
 * small — most nuance lives on the payload, not the event name.
 *
 * The bridge tracks last-seen state per-file so it can emit deltas
 * (`chunk_completed` fires ONLY when bytesUploaded actually advances, not on
 * every push from the decay-tick sampler).
 *
 * @param {import('../diagnostics/UploadDiagnostics').UploadDiagnostics} diagnostics
 * @param {{ subscribePerFileProgress?: Function }} chunkedUploadService
 *   Passed in (rather than statically imported) to break the cycle:
 *   ChunkedUploadService is what calls announceUpload() in the first place.
 * @returns {() => void} disposer
 */
export const attachChunkedUploadBridge = (diagnostics, chunkedUploadService) => {
  const uploadId = diagnostics.uploadId;
  const disposers = [];

  // ── 1. Package-level status stream ───────────────────────────────────────
  let lastStatus = null;
  const unsubTelemetry = uploadTelemetryService.subscribe(uploadId, (state) => {
    if (!state) return;

    diagnostics.patchState({
      bytesUploaded: state.uploadedBytes ?? diagnostics.getState().bytesUploaded,
      totalBytes: state.totalBytes ?? diagnostics.getState().totalBytes,
    });

    if (state.status === lastStatus) return;
    const previous = lastStatus;
    lastStatus = state.status;

    switch (state.status) {
      case uploadTelemetryService.STATUS.UPLOADING:
        if (previous == null || previous === uploadTelemetryService.STATUS.WAITING) {
          // First byte movement, or leaving the queue for an upload slot —
          // either way the upload starts now (UPLOAD_STARTED's implicit patch
          // also resets the stuck clock, so time spent queued doesn't count).
          diagnostics.record(UPLOAD_EVENT.UPLOAD_STARTED, {
            totalBytes: state.totalBytes,
          });
        } else if (
          previous === uploadTelemetryService.STATUS.PAUSED ||
          previous === uploadTelemetryService.STATUS.STALLED
        ) {
          diagnostics.record(UPLOAD_EVENT.UPLOAD_RESUMED, {
            previousStatus: previous,
          });
        }
        break;
      case uploadTelemetryService.STATUS.PAUSED:
        diagnostics.record(UPLOAD_EVENT.UPLOAD_PAUSED, {
          previousStatus: previous,
        });
        break;
      case uploadTelemetryService.STATUS.WAITING:
        // Queued for an upload slot — no bytes will move, and computeHealth
        // exempts "queued" from the stuck rule. Without this case the state
        // stayed "uploading"/"created" and queued uploads were flagged stuck
        // after 60s.
        diagnostics.patchState({ uploaderState: "queued" });
        break;
      case uploadTelemetryService.STATUS.FINALIZING:
        // "finalizing" is exempt from the stuck rule — server-side chunk
        // assembly can legitimately take minutes with zero byte progress.
        diagnostics.patchState({ uploaderState: "finalizing" });
        diagnostics.info("upload_finalizing", {
          bytesUploaded: state.uploadedBytes,
        });
        break;
      case uploadTelemetryService.STATUS.COMPLETED:
        diagnostics.record(UPLOAD_EVENT.UPLOAD_COMPLETED, {
          bytesUploaded: state.uploadedBytes,
          durationMs: (state.elapsedTime || 0) * 1000,
          averageBps: state.averageBps,
        });
        break;
      case uploadTelemetryService.STATUS.FAILED:
        diagnostics.record(UPLOAD_EVENT.UPLOAD_FAILED, {
          previousStatus: previous,
          bytesUploaded: state.uploadedBytes,
        });
        break;
      default:
        break;
    }
  });
  disposers.push(unsubTelemetry);

  // ── 2. Per-file granularity ─────────────────────────────────────────────
  if (chunkedUploadService?.subscribePerFileProgress) {
    /** @type {Map<string, { bytesUploaded: number, retries: number, stallState: string | null, status: string }>} */
    const lastByFile = new Map();

    const unsubPerFile = chunkedUploadService.subscribePerFileProgress(
      uploadId,
      (files) => {
        if (!Array.isArray(files)) return;
        diagnostics.patchState({ chunkCount: files.length });

        for (const file of files) {
          const key = file.sourceFileId;
          if (!key) continue;
          const prev = lastByFile.get(key) || {
            bytesUploaded: 0,
            retries: 0,
            stallState: null,
            status: null,
          };

          // chunk_completed emits on forward byte movement. Threshold prevents
          // sub-KB spam from the decay-tick sampler.
          if (
            typeof file.loaded === "number" &&
            file.loaded > prev.bytesUploaded + 1024
          ) {
            diagnostics.record(UPLOAD_EVENT.CHUNK_COMPLETED, {
              sourceFileId: key,
              originalName: file.originalName,
              bytesUploaded: file.loaded,
              totalBytes: file.sizeBytes,
              status: file.status,
            });
            prev.bytesUploaded = file.loaded;
          }

          if (
            typeof file.retries === "number" &&
            file.retries > prev.retries
          ) {
            diagnostics.record(UPLOAD_EVENT.RETRY_STARTED, {
              sourceFileId: key,
              originalName: file.originalName,
              retryCount: file.retries,
            });
            prev.retries = file.retries;
          }

          if (file.stallState !== prev.stallState) {
            if (
              file.stallState === "stalled" ||
              file.stallState === "reconnecting"
            ) {
              diagnostics.record(UPLOAD_EVENT.CHUNK_FAILED, {
                sourceFileId: key,
                originalName: file.originalName,
                stallState: file.stallState,
                reason: "watchdog_stall",
              });
            } else if (
              (prev.stallState === "stalled" ||
                prev.stallState === "reconnecting") &&
              file.stallState == null
            ) {
              diagnostics.record(UPLOAD_EVENT.RETRY_COMPLETED, {
                sourceFileId: key,
                originalName: file.originalName,
              });
            }
            prev.stallState = file.stallState;
          }

          prev.status = file.status;
          lastByFile.set(key, prev);
        }
      }
    );
    if (typeof unsubPerFile === "function") disposers.push(unsubPerFile);
  }

  return () => {
    for (const d of disposers) {
      try {
        d();
      } catch (_error) {
        /* ignore */
      }
    }
  };
};
